import * as React from "react";
import useArticleFormModel from "../hooks/useArticleFormModel";
import useModelState from "../hooks/useModelState";
import ArticleForm from "../shared/ArticleForm";
import PreviewButton from "./PreviewButton";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useParams } from "react-router-dom";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

export default function Edit() {
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [article, setArticle] = useArticleFormModel();
  const [modelState, setModelState] = useModelState();
  const { id: stringId } = useParams<{ id: string }>();
  const id = parseInt(stringId!);
  const api = useApi();

  React.useEffect(() => {
    async function fetchArticle() {
      setLoading(true);

      try {
        const response = await api.admin.get(`api/kb/articles?id=${id}`);
        if (response.status === 404) {
          console.error("Article not found");
          return;
        }
        const result = await response.json();

        setArticle({
          title: result.Title ?? "",
          slug: result.Slug ?? "",
          topicId: result.TopicId ?? "",
          description: result.Description ?? "",
          content: result.Content ?? "",
          searchTags: result.SearchTags ?? "",
          visibility: result.Visibility ?? 0,
          isPublished: result.IsPublished ?? false,
          url: result.Url ?? "",
          displayPriority: result.DisplayPriority ?? 0,
          botSummary: result.BotSummary ?? "",
          enableBotResponse: result.EnableBotResponse ?? false
        });
      } catch (err) {
        console.error("Failed to fetch article:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchArticle();
  }, [id, setArticle]);

  async function save(publish: boolean) {
    setSaving(true);

    try {
      const resp = await api.admin.put(
        `api/kb/articles?id=${encodeURIComponent(id)}`,
        {
          ...article,
          isPublished: publish
        }
      );

      if (resp.status === 400) {
        console.error(resp);
        setSaving(false);
        return;
      }

      window.location.href = "/kb/articles";
    } catch (err) {
      console.error("Failed to save article:", err);
      setSaving(false);
    }
  }

  async function deleteArticle() {
    if (confirm("Are you sure you want to delete this article?")) {
      try {
        await api.admin.delete(`api/kb/articles?id=${encodeURIComponent(id)}`);
        window.location.href = "/kb/articles";
      } catch (err) {
        console.error("Failed to delete article:", err);
      }
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Edit Article
          </h1>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline"
            >
              {article.url}
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <PreviewButton title={article.title} content={article.content} />
          <button
            className="px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
            onClick={() => deleteArticle()}
            aria-label="Delete article"
          >
            <i className="fa fa-trash mr-1" />
            Delete
          </button>
        </div>
      </div>

      <ArticleForm
        article={article}
        onChange={(props) => {
          setArticle((oldState) => ({ ...oldState, ...props }));
        }}
        modelState={modelState}
      />

      <div className="flex items-center gap-3 mt-6 pt-4 border-t">
        {article.isPublished && (
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save"}
            disabled={saving}
            onClick={() => save(true)}
          />
        )}
        {article.isPublished === false && (
          <>
            <SecondaryBlueButton
              label={saving ? "Saving..." : "Save Draft"}
              disabled={saving}
              onClick={() => save(false)}
            />
            <PrimaryBlueButton
              label={saving ? "Saving..." : "Save and Publish"}
              disabled={saving}
              onClick={() => save(true)}
            />
          </>
        )}
        <a
          href="/kb/articles"
          className="text-sm text-slate-500 hover:text-slate-700 ml-2"
        >
          Back to Articles
        </a>
      </div>
    </div>
  );
}
